import { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Inbox, Mail, Phone, UserCheck, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function PublicLeads() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ kind: "lead" });
      if (search.trim()) params.set("q", search.trim());
      const { data } = await api.get(`/clients?${params.toString()}`);
      setItems(Array.isArray(data) ? data : []);
      setSelectedIds(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const promote = async (c) => {
    if (!window.confirm(`Promuovere "${c.surname || ""} ${c.name || ""}" a cliente effettivo dell'agenzia?`)) return;
    try {
      await api.post(`/clients/${c.id}/promote-to-agency`);
      toast.success("Lead promosso a cliente agenzia");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleOne = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = (checked) => setSelectedIds(checked ? new Set(items.map((c) => c.id)) : new Set());

  const deleteOne = async (c) => {
    if (!window.confirm(`Eliminare definitivamente il lead "${c.surname || ""} ${c.name || ""}"?\n\nL'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Lead eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteBulk = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminare definitivamente ${selectedIds.size} lead selezionat${selectedIds.size === 1 ? "o" : "i"}?\n\nL'operazione non è reversibile.`)) return;
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const id of Array.from(selectedIds)) {
      try { await api.delete(`/clients/${id}`); ok += 1; }
      catch { fail += 1; }
    }
    setBulkBusy(false);
    if (ok > 0) toast.success(`${ok} lead eliminati${fail > 0 ? ` · ${fail} falliti` : ""}`);
    else if (fail > 0) toast.error(`${fail} eliminazioni fallite`);
    load();
  };

  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < items.length;

  return (
    <div className="space-y-6" data-testid="public-leads-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
          <Inbox className="h-8 w-8 sm:h-10 sm:w-10 text-amber-600 bg-amber-100 rounded-lg p-1.5" />
          Lead in attesa
        </h1>
        <p className="text-slate-500 mt-1">
          📋 Archivio dei <strong>contatti acquisiti</strong> da preventivi (richieste online, dal CRM o da offerte) <strong>non ancora confermati</strong>.
          <br />
          <span className="text-xs">Diventano clienti effettivi <strong>automaticamente</strong> quando un&apos;offerta collegata viene pagata o una polizza viene emessa per loro. Puoi anche promuoverli manualmente.</span>
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Input
          data-testid="leads-search"
          placeholder="Cerca per nome, email, cellulare…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          className="max-w-sm"
        />
        <Button data-testid="leads-search-btn" onClick={load}>Cerca</Button>
        {selectedIds.size > 0 && (
          <Button
            data-testid="leads-bulk-delete-btn"
            variant="destructive"
            disabled={bulkBusy}
            onClick={deleteBulk}
            className="inline-flex items-center gap-1.5"
          >
            <Trash2 className="h-4 w-4" /> Elimina selezionati ({selectedIds.size})
          </Button>
        )}
        <div className="ml-auto text-sm text-slate-500 self-center">
          {items.length} {items.length === 1 ? "lead" : "leads"}
          {selectedIds.size > 0 && <span className="ml-2 text-blue-700 font-semibold">· {selectedIds.size} selezionat{selectedIds.size === 1 ? "o" : "i"}</span>}
        </div>
      </div>

      {loading && <div className="text-slate-500">Caricamento…</div>}

      {!loading && items.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500">
          <Inbox className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          Nessun lead in archivio.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    data-testid="leads-select-all"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                    className="h-4 w-4 cursor-pointer"
                    title="Seleziona/deseleziona tutti"
                  />
                </th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Nominativo</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Contatti</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Città</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Richiesto il</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`lead-row-${c.id}`}
                  className={`border-b border-slate-100 hover:bg-slate-50/60 ${selectedIds.has(c.id) ? "bg-blue-50/40" : ""}`}
                >
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      data-testid={`lead-select-${c.id}`}
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggleOne(c.id)}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-2.5 font-medium uppercase">
                    {c.surname} {c.name}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-blue-700"><Mail className="h-3 w-3" /> {c.email}</a>}
                    {c.mobile && <div className="text-xs inline-flex items-center gap-1 mt-1"><Phone className="h-3 w-3" /> {c.mobile}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{c.city || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500 tabular-nums">{formatDate(c.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        data-testid={`lead-view-quotes-${c.id}`}
                        onClick={() => navigate(`/quotes?client_id=${c.id}`)}
                        className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 inline-flex items-center gap-1"
                        title="Vedi preventivi del lead"
                      >
                        Preventivi <ArrowRight className="h-3 w-3" />
                      </button>
                      <button
                        data-testid={`lead-promote-${c.id}`}
                        onClick={() => promote(c)}
                        className="text-xs px-2 py-1 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium inline-flex items-center gap-1"
                        title="Promuovi a cliente effettivo dell'agenzia"
                      >
                        <UserCheck className="h-3.5 w-3.5" /> Promuovi
                      </button>
                      <button
                        data-testid={`lead-delete-${c.id}`}
                        onClick={() => deleteOne(c)}
                        className="text-xs px-2 py-1 rounded border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-700 font-medium inline-flex items-center gap-1"
                        title="Elimina lead"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
